/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import PropTypes from 'prop-types';
import { AlertCircle, ArrowLeft, Camera, Check, CheckCircle, Clock, Copy, Download, Eye, Hash, Link, MapPin, Video, X, Edit2, Send, Maximize2, Trash2, Info, FileText, Music, StickyNote, User } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import Modal from '../components/Modal';
import QRCodeDisplay from '../components/QRCode';
import StatusBadge from '../components/StatusBadge';
import toastService from '../utils/toastService';
import PermissionGuard from '../components/PermissionGuard';
import PhotoGallery from '../components/capture/PhotoGallery';
import { getMediaCategory, getFileExtension, isBrowserNativeVideo } from '../constants/mimeMap';
import { resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';
import { toLocal } from '../utils/dateTime';
import Papa from 'papaparse';
import fileAttachedPin from '../assets/file-attached-pin.svg';
import filesIcon from '../assets/files.svg';
import scannerIcon from '../assets/scanner.svg';

const COPY_RESET_DELAY_MS = 2000;

const typeIcons = { photo: Camera, video: Video, identifier: Hash };

const triggerDownload = async (url, fileName) => {
  const isVideo = /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url);

  // 👉 For videos: DON'T use fetch (avoids memory + streaming issues)
  if (isVideo) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `video_${Date.now()}`;
    a.rel = 'noopener noreferrer';

    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }

  // 👉 For other files: use blob
  try {
    const response = await fetch(url, { mode: 'cors' });

    if (!response.ok) {
      throw new Error('Fetch failed');
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName || `download_${Date.now()}`;
    document.body.appendChild(a);
    a.click();

    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed, falling back to direct link:', error);

    // fallback
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

const TextPreview = ({ url }) => {
  const [text, setText] = useState('Loading text preview...');

  useEffect(() => {
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch text file');
        return res.text();
      })
      .then((data) => setText(data))
      .catch((err) => setText(`Error loading text preview: ${err.message}`));
  }, [url]);

  return (
    <div style={{ width: '80vw', height: '80vh', background: '#1e1e1e', color: '#fff', padding: '20px', borderRadius: '8px', overflow: 'auto', textAlign: 'left' }}>
      <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {text}
      </pre>
    </div>
  );
};

const CsvPreview = ({ url }) => {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Papa.parse(url, {
      download: true,
      header: false,
      preview: 50, // Limit to first 50 rows for snappy preview performance
      complete: (results) => {
        setRows(results.data);
        setLoading(false);
      },
      error: (err) => {
        setError(err.message);
        setLoading(false);
      },
    });
  }, [url]);

  if (loading) return <p className="text-white">Loading CSV preview...</p>;
  if (error) return <p className="text-red-500">Error loading CSV: {error}</p>;

  return (
    <div style={{ width: '90vw', maxHeight: '80vh', background: '#fff', color: '#000', borderRadius: '8px', overflow: 'auto', padding: '10px' }}>
      <span style={{ marginBottom: "8px", color: 'red' }}>Note: We are showing only first 50 rows of the file</span>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} style={{ borderBottom: '1px solid #ddd' }}>
              {row.map((cell, cellIndex) => (
                rowIndex === 0 ? (
                  <th key={cellIndex} style={{ padding: '10px', background: '#f4f4f4', fontWeight: 'bold', position: 'sticky', top: 0 }}>
                    {cell}
                  </th>
                ) : (
                  <td key={cellIndex} style={{ padding: '10px' }}>
                    {cell}
                  </td>
                )
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const timeToSeconds = (time) => {
  const [h, m, s] = time.split(':');

  return (
    Number(h) * 3600 +
    Number(m) * 60 +
    parseFloat(s.replace(',', '.'))
  );
}

const parseVtt = (content) => {
  const captions = [];

  const blocks = content
    .replace(/\r/g, '')
    .split('\n\n');

  for (const block of blocks) {
    const lines = block.trim().split('\n');

    if (!lines.length) continue;
    if (lines[0] === 'WEBVTT') continue;

    let timeLineIndex = 0;

    // Skip cue identifier if present
    if (!lines[0].includes('-->')) {
      timeLineIndex = 1;
    }

    if (!lines[timeLineIndex]) continue;

    const match = lines[timeLineIndex].match(
      /([\d:.]+)\s+-->\s+([\d:.]+)/
    );

    if (!match) continue;

    captions.push({
      start: timeToSeconds(match[1]),
      end: timeToSeconds(match[2]),
      text: lines.slice(timeLineIndex + 1).join('\n')
    });
  }

  return captions;
}

// Main Component
const FullScreenViewer = ({ url, type, mimeType, fileName, transcriptionUrl, onClose, duration }) => {

  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const hasResolvedDuration = useRef(false);
  const [trackBlobUrl, setTrackBlobUrl] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [currentCaption, setCurrentCaption] = useState(null);
  const videoRef = useRef(null);
  const activeCaptionRef = useRef(null);

  useEffect(() => {
    if (!transcriptionUrl) return;

    let objectUrl = '';

    fetch(transcriptionUrl)
      .then(async (res) => {
        const text = await res.text();

        // Create blob URL for the <track>
        const blob = new Blob([text], { type: "text/vtt" });
        objectUrl = URL.createObjectURL(blob);
        setTrackBlobUrl(objectUrl);

        // Parse transcript
        setTranscript(parseVtt(text));
      })
      .catch(console.error);

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [transcriptionUrl]);

  useEffect(() => {
    if (activeCaptionRef.current) {
      activeCaptionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentCaption]);

  const handleTimeUpdate = () => {
    const t = videoRef.current?.currentTime;
    if (t == null) return;
    const active = transcript.find(c => t >= c.start && t <= c.end) || null;
    setCurrentCaption(active);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const track = video.textTracks?.[0];
    if (!track) return;
    track.mode = showCaptions ? 'showing' : 'hidden';
  }, [showCaptions, trackBlobUrl]);

  const extension = getFileExtension(fileName)?.toLowerCase();

  const effectiveCategory = type === 'file'
    ? getMediaCategory(mimeType, fileName)
    : (type === 'video' ? 'video' : 'image');

  const isVideo = effectiveCategory === 'video';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 40, right: 20, zIndex: 10000, background: 'rgba(255, 255, 255, 0.79)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black' }}
      >
        <X size={20} />
      </button>

      {isVideo && transcript.length > 0 && (
        <button
          onClick={e => { e.stopPropagation(); setShowCaptions(v => !v); }}
          style={{ position: 'absolute', top: 40, right: 185, zIndex: 10000, background: showCaptions ? 'rgba(99,102,241,0.9)' : 'rgba(255,255,255,0.79)', border: 'none', borderRadius: '20px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: showCaptions ? 'white' : 'black', fontSize: 12, fontWeight: 700 }}
        >
          CC
        </button>
      )}

      {isVideo && transcript.length > 0 && (
        <button
          onClick={e => { e.stopPropagation(); setShowTranscript(v => !v); }}
          style={{ position: 'absolute', top: 40, right: 72, zIndex: 10000, background: showTranscript ? 'rgba(99,102,241,0.9)' : 'rgba(255,255,255,0.79)', border: 'none', borderRadius: '20px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: showTranscript ? 'white' : 'black', fontSize: 12, fontWeight: 700 }}
        >
          <FileText size={14} />
          Transcript
        </button>
      )}

      <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
        <div style={{ flex: showTranscript && isVideo ? '0 0 65%' : '1', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

      {effectiveCategory === 'video' ? (
        videoError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
            <Video size={36} className="text-base-content/30" strokeWidth={1.5} />
            <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this video type</p>
          </div>
        ) : (
          <>
            {videoLoading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
              </div>
            )}
            <video
              ref={videoRef}
              onLoadedMetadata={(e) => {
                const v = e.target;
                if (!Number.isFinite(v.duration)) {
                  v.currentTime = Number.MAX_SAFE_INTEGER;
                }
              }}
              onDurationChange={(e) => {
                const v = e.target;
                if (!hasResolvedDuration.current && Number.isFinite(v.duration)) {
                  hasResolvedDuration.current = true;
                  v.currentTime = 0;
                }
              }}
              onCanPlayThrough={(e) => { e.target.style.opacity = 1; setVideoLoading(false); }}
              src={url}
              controls
              autoPlay
              playsInline
              onClick={e => e.stopPropagation()}
              style={{ width: '100vw', height: '100vh', objectFit: 'contain', opacity: 0, transition: 'opacity 0.2s' }}
              onError={() => setVideoError(true)}
              onTimeUpdate={handleTimeUpdate}
            >
              {trackBlobUrl && (
                <track src={trackBlobUrl} kind="subtitles" srcLang="en" label="English" default />
              )}
            </video>
          </>
        )
      ) : effectiveCategory === 'image' ? (
        imageError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
            <Camera size={36} className="text-base-content/30" strokeWidth={1.5} />
            <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this image type</p>
          </div>
        ) : (
          <img src={url} alt="Full view" onClick={e => e.stopPropagation()} style={{ width: '100vw', height: '100vh', objectFit: 'contain' }} onError={() => setImageError(true)} />
        )
      ) : (
        <div className="text-white" onClick={e => e.stopPropagation()}>
          {effectiveCategory === 'audio' ? (
            audioError ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
                <Music size={36} className="text-base-content/30" strokeWidth={1.5} />
                <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this audio type</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <Music size={64} strokeWidth={1} />
                <audio
                  controls
                  preload="metadata"
                  src={url}
                  onError={() => setAudioError(true)}
                />
                <p className="text-sm opacity-60">{mimeType || fileName}</p>
              </div>
            )
          ) : effectiveCategory === 'document' ? (
            <>
              {extension === 'pdf' && (
                <iframe
                  src={url}
                  style={{ width: '90vw', height: '100vh', border: 'none' }}
                  title="PDF Preview"
                />
              )}

              {extension === 'txt' && (
                <TextPreview url={url} />
              )}

              {extension === 'csv' && (
                <CsvPreview url={url} />
              )}
              {!['pdf', 'txt', 'csv'].includes(extension) && (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200" onClick={e => e.stopPropagation()}>
                  <FileText size={36} className="text-base-content/30" strokeWidth={1.5} />
                  <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Preview not available for this file type</p>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
              <FileText size={36} className="text-base-content/30" strokeWidth={1.5} />
              <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Unsupported file type</p>
            </div>
          )}
        </div>
      )}

        </div>

        {showTranscript && isVideo && transcript.length > 0 && (
          <div
            onClick={e => e.stopPropagation()}
            style={{ flex: '0 0 35%', height: '100vh', overflowY: 'auto', background: 'rgba(15,15,15,0.95)', borderLeft: '1px solid rgba(255,255,255,0.1)', padding: '60px 20px 20px' }}
          >
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 16 }}>Transcript</p>
            {transcript.map((item, index) => {
              const isActive = currentCaption === item;
              return (
                <div
                  key={index}
                  ref={isActive ? activeCaptionRef : null}
                  onClick={() => { if (videoRef.current) videoRef.current.currentTime = item.start; }}
                  style={{ padding: '10px 12px', marginBottom: 6, borderRadius: 8, cursor: 'pointer', background: isActive ? 'rgba(99,102,241,0.25)' : 'transparent', borderLeft: isActive ? '3px solid rgb(99,102,241)' : '3px solid transparent', transition: 'all 0.2s' }}
                >
                  <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 600, marginBottom: 4 }}>
                    {new Date(item.start * 1000).toISOString().substr(11, 8)}
                  </p>
                  <p style={{ color: isActive ? 'white' : 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 1.5 }}>{item.text}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

FullScreenViewer.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
  transcriptionUrl: PropTypes.string,
  onClose: PropTypes.func,
};

const ProvenanceTimeline = ({ events, loading, error }) => {
  const eventLabels = {
    'assignment.created': 'Assignment Created',
    'assignment.link_opened': 'Link Opened',
    'assignment.capture_started': 'Capture Started',
    'assignment.capture_completed': 'Item Captured',
    'assignment.capture_skipped': 'Item Skipped',
    'assignment.completed': 'Submission Complete',
    'assignment.expired': 'Link Expired',
    'assignment.updated': 'Assignment Updated',
    'assignment.otp_sent': 'OTP Sent',
    'assignment.otp_resent': 'OTP Resent',
    'assignment.otp_verified': 'OTP Verified',
  };

  const getEventLabel = (ev) => {
    if (ev.event === 'assignment.capture_completed' && ev.capturedByName) {
      return `Item Captured By ${ev.capturedByName}`;
    }
    return eventLabels[ev.event] || ev.event;
  };

  if (loading) {
    return (
      <div className="bg-base-100 border border-base-300 rounded-xl p-6">
        <h3 className="font-black text-base-content uppercase tracking-tight mb-4">Activity Timeline</h3>
        <div className="flex items-center gap-2 text-base-content/40 text-sm">
          <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-base-100 border border-base-300 rounded-xl p-6">
        <h3 className="font-black text-base-content uppercase tracking-tight mb-4">Activity Timeline</h3>
        <p className="text-error text-sm font-bold">{error}</p>
      </div>
    );
  }

  if (!events || events.length === 0) { return null; }

  return (
    <div className="bg-base-100 border border-base-300 rounded-xl p-6 mt-6">
      <h3 className="font-black text-base-content uppercase tracking-tight mb-4">Activity Timeline</h3>
      <div className="space-y-3">
        {events.map((ev, idx) => {
          const isSkipped = ev.event === 'assignment.capture_skipped';
          return (
            <div key={idx} className="flex gap-3">
              <div className="flex flex-col items-center">
                {isSkipped ? (
                  // Grey hollow-style dot for skipped events
                  <div className="w-2.5 h-2.5 rounded-full bg-base-content/20 mt-1 flex-shrink-0" />
                ) : (
                  // Blue glowing dot for normal events
                  <div className="w-2.5 h-2.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--p)/0.5)] mt-1 flex-shrink-0" />
                )}
                {idx < events.length - 1 && <div className="w-px flex-1 bg-base-300 mt-0" />}
              </div>
              <div className="pb-2 min-w-0">
                <p className={`text-sm font-semibold tracking-tight ${isSkipped ? 'text-base-content/20' : 'text-base-content'
                  }`}>
                  {getEventLabel(ev)}
                  {(ev.event === 'assignment.capture_completed' || ev.event === 'assignment.capture_skipped') && ev.detail
                    ? ` - ${ev.detail}`
                    : ''}
                </p>
                <p className={`text-[10px] font-semibold tracking-widest mt-0.5 ${isSkipped ? 'text-base-content/20' : 'text-base-content/40'
                  }`}>
                  {toLocal(ev.timestamp, { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ProvenanceTimeline.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      event: PropTypes.string,
      detail: PropTypes.string,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      capturedByName: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.string,
};

const ProvenanceMetadata = ({ provenanceData }) => {
  const [expanded, setExpanded] = useState(false);

  if (!provenanceData || provenanceData.length === 0) { return null; }

  const hasMore = provenanceData.length > 1;

  const formatEventType = (type) => {
    const labels = {
      'assignment_opened': 'Assignment Opened',
      'media_captured': 'Media Captured'
    };
    return labels[type] || type;
  };

  const renderCard = (event, idx) => (
    <div key={idx} className="border border-base-300 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-primary uppercase tracking-wider">
          {formatEventType(event.eventType)}
        </span>
        <span className="text-[10px] font-semibold text-base-content/40 tracking-widest">
          {toLocal(event.timestamp)}
        </span>
      </div>
      <div className="space-y-3">
        {event.network && (
          <div>
            <p className="text-[10px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Network</p>
            <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
              {event.network.ipAddress && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">IP Address:</span>
                  <span className="text-base-content font-mono font-semibold">{event.network.ipAddress}</span>
                </div>
              )}
              {event.network.connectionType && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">Connection:</span>
                  <span className="text-base-content font-semibold uppercase">{event.network.connectionType}</span>
                </div>
              )}
            </div>
          </div>
        )}
        {event.device && (
          <div>
            <p className="text-[10px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Device</p>
            <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
              {event.device.os && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">OS:</span>
                  <span className="text-base-content font-semibold">{event.device.os}</span>
                </div>
              )}
              {event.device.browser && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">Browser:</span>
                  <span className="text-base-content font-semibold">{event.device.browser}</span>
                </div>
              )}
              {event.device.deviceType && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">Type:</span>
                  <span className="text-base-content font-semibold capitalize">{event.device.deviceType}</span>
                </div>
              )}
            </div>
          </div>
        )}
        {event.location && event.location.granted && (
          <div>
            <p className="text-[10px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Location</p>
            <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-base-content/60 font-medium">Coordinates:</span>
                <span className="text-base-content font-mono font-semibold">
                  {event.location.latitude.toFixed(6)}, {event.location.longitude.toFixed(6)}
                </span>
              </div>
              {event.location.accuracy && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">Accuracy:</span>
                  <span className="text-base-content font-semibold">{Math.round(event.location.accuracy)}m</span>
                </div>
              )}
              {event.location.source && (
                <div className="flex justify-between text-xs">
                  <span className="text-base-content/60 font-medium">Source:</span>
                  <span className="text-base-content font-semibold uppercase">{event.location.source}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-base-100 border border-base-300 rounded-xl flex flex-col overflow-hidden">
      <div
        className="p-6"
        style={{
          maxHeight: expanded ? '900px' : '450px',
          overflowY: expanded ? 'auto' : 'visible',
        }}
      >
        <h3 className="font-black text-base-content uppercase tracking-tight mb-4">Provenance Metadata</h3>
        <div className="space-y-4">
          {provenanceData.slice(0, 1).map((event, idx) => renderCard(event, idx))}
        </div>
        {hasMore && (
          <div
            style={{
              display: 'grid',
              gridTemplateRows: expanded ? '1fr' : '0fr',
              transition: 'grid-template-rows 0.35s ease',
            }}
          >
            <div style={{ overflow: 'hidden' }}>
              <div className="space-y-4 pt-4">
                {provenanceData.slice(1).map((event, idx) => renderCard(event, idx + 1))}
              </div>
            </div>
          </div>
        )}
      </div>
      {hasMore && (
        <button
          onClick={() => setExpanded(prev => !prev)}
          className="w-full text-[10px] font-black uppercase tracking-widest text-primary hover:text-primary/70 transition-colors border-t border-base-300 py-3 bg-base-100"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

ProvenanceMetadata.propTypes = {
  provenanceData: PropTypes.arrayOf(
    PropTypes.shape({
      eventType: PropTypes.string,
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      network: PropTypes.shape({
        ipAddress: PropTypes.string,
        connectionType: PropTypes.string,
      }),
      device: PropTypes.shape({
        os: PropTypes.string,
        browser: PropTypes.string,
        deviceType: PropTypes.string,
      }),
      location: PropTypes.shape({
        granted: PropTypes.bool,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        accuracy: PropTypes.number,
        source: PropTypes.string,
      }),
    })
  ),
};


const ProvenanceVideoThumb = ({ file, duration, onError }) => {
  const [error, setError] = useState(false);
  const isTranscodedMp4 = /\.mp4(\?|$)/i.test(file.fileUrl || '');
  const isNative = isBrowserNativeVideo(file.mimeType, file.fileName) || isTranscodedMp4;
  const [loading, setLoading] = useState(true);
  const hasResolvedDuration = useRef(false);

  useEffect(() => {
    if (!isNative || error) onError?.();
  }, [isNative, error, onError]);

  if (!isNative || error) {
    return (
      <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
        <img
          src={fileAttachedPin}
          alt="attachment"
          className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
        />
        <span className="text-[15px] md:text-[15px] text-base-content truncate">
          {file.fileName || 'file'}
        </span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin" />
        </div>
      )}
      <video
        src={file.fileUrl}
        controls
        playsInline
        style={{ opacity: 0 }}
        onLoadedMetadata={(e) => {
          const v = e.target;
          if (!Number.isFinite(v.duration)) {
            v.currentTime = Number.MAX_SAFE_INTEGER;
          }
        }}
        onDurationChange={(e) => {
          const v = e.target;
          if (!hasResolvedDuration.current && Number.isFinite(v.duration)) {
            hasResolvedDuration.current = true;
            v.currentTime = 0;
          }
        }}
        onCanPlayThrough={(e) => { e.target.style.opacity = 1; setLoading(false); }}
        onError={() => setError(true)}
        onClick={e => e.stopPropagation()}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-200"
      />
    </div>
  );
};

ProvenanceVideoThumb.propTypes = { file: PropTypes.object, duration: PropTypes.number, onError: PropTypes.func };

const ProvenanceImageThumb = ({ file, onError }) => {
  const [error, setError] = useState(false);

  const handleError = () => {
    setError(true);
    onError?.();
  };

  return (
    <div className="relative w-full h-full">
      {error ? (
        <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
          <img
            src={fileAttachedPin}
            alt="attachment"
            className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
          />
          <span className="text-[15px] md:text-[15px] text-base-content truncate">
            {file.fileName || 'file'}
          </span>
        </div>
      ) : (
        <img src={file.fileUrl} alt="preview" className="w-full h-full object-cover" onError={handleError} />
      )}
    </div>
  );
};

ProvenanceImageThumb.propTypes = { file: PropTypes.object, onError: PropTypes.func };

const ProvenanceMediaCard = ({ file, downloading, onDownload, onViewFullscreen }) => {
  const provenance = file.provenanceData || {};
  const duration = parseInt(file.duration);
  const cat = getMediaCategory(file.mimeType, file.fileName);
  const [mediaError, setMediaError] = useState(false);
  const isTranscodedMp4 = /\.mp4(\?|$)/i.test(file.fileUrl || '');
  const isNativeVideo = cat === 'video' && (isBrowserNativeVideo(file.mimeType, file.fileName) || isTranscodedMp4);
  const canPreview = (cat === 'image' || isNativeVideo) && !mediaError;
  const Icon = typeIcons[file.fileType] || Camera;

  const formatSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={`bg-base-100 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${file.identifierNeedsReview ? 'border-2 border-error' : 'border-base-content/5 hover:border-primary/10'}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        <div className="relative group">
          {file.identifierNeedsReview && (
            <span
              className="absolute top-2 left-2 z-20 bg-error text-error-content text-[9px] font-black uppercase tracking-wide px-2 py-1 rounded-md shadow-sm"
              title={file.identifierValidationError || 'This value was synced from offline capture and skipped validation.'}
            >
              Needs Review
            </span>
          )}
          {/* Action Buttons */}
          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all z-20">
            {canPreview && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewFullscreen(file.fileUrl, file.fileType, file.mimeType, file.fileName, file.duration, file.transcriptionUrl); }}
                className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all"
                title="View Fullscreen"
              >
                <Eye size={14} />
              </button>
            )}
            <PermissionGuard permissions={['assignments.download_media']}>
              <button
                onClick={onDownload}
                disabled={downloading}
                className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all disabled:opacity-50"
                title="Download Media"
              >
                {downloading ? (
                  <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <Download size={14} />
                )}
              </button>
            </PermissionGuard>
          </div>

          {/* Media */}
          <div className="bg-base-900/5 aspect-[4/3] flex items-center justify-center overflow-hidden relative">
            {(() => {
              if (cat === 'video') return <ProvenanceVideoThumb file={file} duration={duration} onError={() => setMediaError(true)} />;
              if (cat === 'image') return <ProvenanceImageThumb file={file} onError={() => setMediaError(true)} />;
              if (cat === 'audio') return mediaError ? (
                <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
                  <img src={fileAttachedPin} alt="attachment" className="w-[28px] h-[28px] shrink-0 theme-invert-icon" />
                  <span className="text-[15px] md:text-[15px] text-base-content truncate">{file.fileName || 'file'}</span>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
                  <audio className='w-full' controls preload="metadata" src={file.fileUrl} onError={() => setMediaError(true)} />
                </div>
              );
              return (
                <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
                  <img src={fileAttachedPin} alt="attachment" className="w-[28px] h-[28px] shrink-0 theme-invert-icon" />
                  <span className="text-[15px] md:text-[15px] text-base-content truncate">{file.fileName || 'file'}</span>
                </div>
              );
            })()}
          </div>

          {/* Media Info */}
          <div className="p-3 border-t border-base-content/5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <div className="w-5 h-5 bg-primary/5 rounded flex items-center justify-center shrink-0">
                  {file.fileType === "identifier" ? <img src={scannerIcon} alt="" className='w-10 h-10 object-contain' /> : file.fileType === "file" ? <img src={filesIcon} alt="" className='w-10 h-10 object-contain p-1' /> : <Icon size={12} className="text-primary" />}
                </div>
                <span className="text-[10px] font-black text-base-content/60 uppercase tracking-tight truncate">
                  {toLocal(provenance.capturedAt || file.createdAt, { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <span className="text-[10px] font-bold text-base-content/40 uppercase tracking-wide shrink-0">{formatSize(file.fileSize)}</span>
            </div>
            <h4 className="text-[11px] font-black text-base-content uppercase tracking-tight truncate">{file.stepDescription}</h4>
            {file.capturedByName && (
              <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-1.5" title={file.capturedByEmail || undefined}>
                <User size={10} className="shrink-0" />
                <span className="truncate">Captured By: {file.capturedByName}</span>
              </div>
            )}
            {file.identifierText && (
              <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1">
                <span className="truncate" title={file.identifierText}>Captured Value: {file.identifierText}</span>
              </div>
            )}
            {(file.customerIdentifierText ?? file.identifierText) && (
              <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1">
                <span className="truncate" title={file.customerIdentifierText ?? file.identifierText}>Modified Value: {file.customerIdentifierText ?? file.identifierText}</span>
              </div>
            )}
            {provenance.location?.latitude && (
              <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-2">
                <MapPin size={10} className="shrink-0" />
                <span className="truncate">{provenance.location.latitude.toFixed(4)}, {provenance.location.longitude.toFixed(4)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Provenance Data Section */}
        <div className="p-4 bg-base-50 border-t lg:border-t-0 lg:border-l border-base-content/5">
          <h4 className="text-[10px] font-black text-base-content/30 uppercase tracking-widest mb-3">Provenance Data</h4>
          <div className="space-y-3">
            {provenance.network && (
              <div>
                <p className="text-[9px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Network</p>
                <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
                  {provenance.network.ipAddress && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">IP Address:</span>
                      <span className="text-base-content font-mono font-semibold">{provenance.network.ipAddress}</span>
                    </div>
                  )}
                  {provenance.network.connectionType && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">Connection:</span>
                      <span className="text-base-content font-semibold uppercase">{provenance.network.connectionType}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {provenance.device && (
              <div>
                <p className="text-[9px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Device</p>
                <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
                  {provenance.device.os && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">OS:</span>
                      <span className="text-base-content font-semibold">{provenance.device.os}</span>
                    </div>
                  )}
                  {provenance.device.browser && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">Browser:</span>
                      <span className="text-base-content font-semibold">{provenance.device.browser}</span>
                    </div>
                  )}
                  {provenance.device.deviceType && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">Type:</span>
                      <span className="text-base-content font-semibold capitalize">{provenance.device.deviceType}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {provenance.location?.granted && (
              <div>
                <p className="text-[9px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Location</p>
                <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-base-content/60 font-medium">Coordinates:</span>
                    <span className="text-base-content font-mono font-semibold text-[10px]">
                      {provenance.location.latitude.toFixed(6)}, {provenance.location.longitude.toFixed(6)}
                    </span>
                  </div>
                  {provenance.location.accuracy && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">Accuracy:</span>
                      <span className="text-base-content font-semibold">{Math.round(provenance.location.accuracy)}m</span>
                    </div>
                  )}
                  {provenance.location.source && (
                    <div className="flex justify-between text-xs">
                      <span className="text-base-content/60 font-medium">Source:</span>
                      <span className="text-base-content font-semibold uppercase">{provenance.location.source}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ProvenanceMediaCard.propTypes = { file: PropTypes.object, downloading: PropTypes.bool, onDownload: PropTypes.func, onViewFullscreen: PropTypes.func };

/**
 * GallerySlideImage — renders a single image slide inside PhotoGallery with an
 * error fallback so a broken image doesn't silently leave a blank slide.
 */
const GallerySlideImage = ({ photo }) => {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
        <img src={fileAttachedPin} alt="attachment" className="w-[28px] h-[28px] shrink-0 theme-invert-icon" />
        <span className="text-[15px] text-base-content truncate">{photo.fileName || 'file'}</span>
      </div>
    );
  }
  return (
    <img
      src={photo.fileUrl}
      alt="preview"
      className="w-full h-full object-cover"
      onError={() => setError(true)}
    />
  );
};
GallerySlideImage.propTypes = { photo: PropTypes.object.isRequired };

const MediaCard = ({ file, onViewFullscreen, stepOrder }) => {
  const fileUrl = file.fileUrl;
  const duration = parseInt(file.duration);
  const effectiveCategory = getMediaCategory(file.mimeType, file.fileName);
  const Icon = effectiveCategory === 'video' ? Video : effectiveCategory === 'audio' ? Music : effectiveCategory === 'image' ? Camera : FileText;
  const [downloading, setDownloading] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const hasResolvedDuration = useRef(false);

  const isTranscodedMp4 = /\.mp4(\?|$)/i.test(fileUrl || '');
  const isNativeVideo = effectiveCategory === 'video' && (isBrowserNativeVideo(file.mimeType, file.fileName) || isTranscodedMp4);
  const canPreview = (effectiveCategory === 'image' || isNativeVideo) && !mediaError;

  const formatSize = (bytes) => {
    if (!bytes) { return 'Unknown size'; }
    if (bytes < 1024) { return `${bytes} B`; }
    if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloading(true);
    try {
      await triggerDownload(fileUrl, file.fileName);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  const provenance = file.provenanceData || {};

  return (
    <div
      className={`bg-base-100 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all group relative ${file.identifierNeedsReview ? 'border-2 border-error' : 'border-base-content/5 hover:border-primary/10'}`}
      onClick={!canPreview && effectiveCategory !== 'video' ? handleDownload : undefined}
      style={!canPreview && effectiveCategory !== 'video' ? { cursor: 'pointer' } : undefined}
    >
      {file.identifierNeedsReview && (
        <span
          className="absolute top-2 left-2 z-20 bg-error text-error-content text-[9px] font-black uppercase tracking-wide px-2 py-1 rounded-md shadow-sm"
          title={file.identifierValidationError || 'This value was synced from offline capture and skipped validation.'}
        >
          Needs Review
        </span>
      )}
      {/* Action Buttons - Top Right */}
      <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all z-20">
        {canPreview && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onViewFullscreen(fileUrl, file.fileType, file.mimeType, file.fileName, file.duration, file.transcriptionUrl);
            }}
            className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all"
            title="View Fullscreen"
          >
            <Eye size={14} />
          </button>
        )}
        <PermissionGuard permissions={['assignments.download_media']}>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all disabled:opacity-50"
            title="Download Media"
          >
            {downloading ? (
              <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            ) : (
              <Download size={14} />
            )}
          </button>
        </PermissionGuard>
      </div>

      {/* Media Preview */}
      <div className="bg-base-900/5 aspect-[4/3] flex items-center justify-center overflow-hidden relative">
        {effectiveCategory === 'video' ? (
          (mediaError) ? (
            <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
              <img
                src={fileAttachedPin}
                alt="attachment"
                className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
              />
              <span className="text-[15px] md:text-[15px] text-base-content truncate">
                {file.fileName || 'file'}
              </span>
            </div>
          ) : !isBrowserNativeVideo(file.mimeType, file.fileName) && !/\.mp4(\?|$)/i.test(fileUrl || '') ? (
            <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
              <img
                src={fileAttachedPin}
                alt="attachment"
                className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
              />
              <span className="text-[15px] md:text-[15px] text-base-content truncate">
                {file.fileName || 'file'}
              </span>
            </div>
          ) : (
            <>
              {videoLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin" />
                </div>
              )}
              <video
                src={fileUrl}
                controls
                playsInline
                style={{ opacity: 0 }}
                onLoadedMetadata={(e) => {
                  const v = e.target;
                  if (!Number.isFinite(v.duration)) {
                    v.currentTime = Number.MAX_SAFE_INTEGER;
                  }
                }}
                onDurationChange={(e) => {
                  const v = e.target;
                  if (!hasResolvedDuration.current && Number.isFinite(v.duration)) {
                    hasResolvedDuration.current = true;
                    v.currentTime = 0;
                  }
                }}
                onCanPlayThrough={(e) => { e.target.style.opacity = 1; setVideoLoading(false); }}
                onError={() => setMediaError(true)}
                onClick={e => e.stopPropagation()}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-200"
              />
            </>
          )
        ) : effectiveCategory === 'image' ? (
          mediaError ? (
            <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
              <img
                src={fileAttachedPin}
                alt="attachment"
                className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
              />
              <span className="text-[15px] md:text-[15px] text-base-content truncate">
                {file.fileName || 'file'}
              </span>
            </div>
          ) : (
            <img
              src={fileUrl}
              alt="preview"
              className="w-full h-full object-cover"
              onError={() => setMediaError(true)}
            />
          )
        ) : effectiveCategory === 'audio' ? (
          mediaError ? (
            <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
              <img
                src={fileAttachedPin}
                alt="attachment"
                className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
              />
              <span className="text-[15px] md:text-[15px] text-base-content truncate">
                {file.fileName || 'file'}
              </span>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
              <audio className='w-full' controls preload="metadata" src={fileUrl} onError={() => setMediaError(true)} />
            </div>
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
            <img
              src={fileAttachedPin}
              alt="attachment"
              className="w-[28px] h-[28px] shrink-0 theme-invert-icon"
            />
            <span className="text-[15px] md:text-[15px] text-base-content truncate">
              {file.fileName || 'file'}
            </span>
          </div>
        )}
      </div>

      {/* Bottom Section - Icon + Date/Time | Size */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <div className="w-5 h-5 bg-primary/5 rounded flex items-center justify-center shrink-0">
              <Icon size={12} className="text-primary" />
            </div>
            <span className="text-[10px] font-black text-base-content/60 uppercase tracking-tight truncate">
              {toLocal(provenance.capturedAt || file.createdAt, {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          <span className="text-[10px] font-bold text-base-content/40 uppercase tracking-wide shrink-0">
            {formatSize(file.fileSize)}
          </span>
        </div>

        {/* Title */}
        <h4 className="text-[11px] font-black text-base-content uppercase tracking-tight truncate">
          {file.stepDescription}
        </h4>

        {/* Captured By (if available) */}
        {file.capturedByName && (
          <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-1.5" title={file.capturedByEmail || undefined}>
            <User size={10} className="shrink-0" />
            <span className="truncate">Captured By: {file.capturedByName}</span>
          </div>
        )}

        {/* Identifier Text (if available) */}
        {file.identifierText && (
          <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1">
            <span className="truncate" title={file.identifierText}>Captured Value: {file.identifierText}</span>
          </div>
        )}
        {(file.customerIdentifierText ?? file.identifierText) && (
          <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1">
            <span className="truncate" title={file.customerIdentifierText ?? file.identifierText}>Modified Value: {file.customerIdentifierText ?? file.identifierText}</span>
          </div>
        )}
        {/* Location (if available) */}
        {provenance.location?.latitude && (
          <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-2">
            <MapPin size={10} className="shrink-0" />
            <span className="truncate">
              {provenance.location.latitude.toFixed(4)}, {provenance.location.longitude.toFixed(4)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

MediaCard.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fileType: PropTypes.string,
    fileUrl: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fileName: PropTypes.string,
    provenanceData: PropTypes.shape({
      capturedAt: PropTypes.string,
      location: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
      }),
    }),
    createdAt: PropTypes.string,
    fileSize: PropTypes.number,
    stepDescription: PropTypes.string,
    identifierText: PropTypes.string,
    customerIdentifierText: PropTypes.string,
    identifierNeedsReview: PropTypes.bool,
    identifierValidationError: PropTypes.string,
    capturedByName: PropTypes.string,
    capturedByEmail: PropTypes.string,
  }),
  onViewFullscreen: PropTypes.func,
  stepOrder: PropTypes.number,
};

const NOTE_TRUNCATE_LENGTH = 200;

const NoteCard = ({ name, email, timestamp, note }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOverflowing = note.length > NOTE_TRUNCATE_LENGTH;
  const displayText = isOverflowing && !isExpanded
    ? `${note.slice(0, NOTE_TRUNCATE_LENGTH).trimEnd()}...`
    : note;

  return (
    <div className="p-3 rounded-xl bg-base-200/60 border border-base-300">
      <div className="flex justify-between items-start gap-2 mb-1.5">
        <div className="min-w-0">
          <p className="text-sm font-bold text-base-content truncate">{name}</p>
          <p className="text-[11px] text-base-content/50 truncate">{email}</p>
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 whitespace-nowrap">
          {toLocal(timestamp, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p className="text-sm text-base-content custom-line-break">{displayText}</p>
      {isOverflowing && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="mt-1 text-xs font-bold text-primary hover:underline"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

NoteCard.propTypes = {
  name: PropTypes.string,
  email: PropTypes.string,
  timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  note: PropTypes.string,
};

const ReviewMedia = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [viewer, setViewer] = useState(null); // { url, type }
  const [expiring, setExpiring] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [showExpireModal, setShowExpireModal] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [timelineError, setTimelineError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [galleryIndexes, setGalleryIndexes] = useState({});

  const handleIndexChange = (stepKey, newIndex) => {
    setGalleryIndexes(prev => ({ ...prev, [stepKey]: newIndex }));
  };

  const getCaptureLink = (assignmentData) => {
    // If short_url exists and is not null/empty, use it
    if (assignmentData?.shortUrl) {
      return assignmentData.shortUrl;
    }

    // Otherwise, construct the full URL using the token
    const base = process.env.REACT_APP_CAPTURE_BASE_URL || 'http://localhost:3000';
    return `${base}/capture/${assignmentData?.secureToken || ''}`;
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = link;
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.warn('Copy fallback failed:', err);
      }
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
  };

  const handleViewFullscreen = (url, type, mimeType, fileName, duration, transcriptionUrl) => {
    setViewer({ url, type, mimeType, fileName, duration: Number(duration), transcriptionUrl });
  }

  const handleExpireToggle = () => {
    setShowExpireModal(true);
  };

  const handleExpireConfirm = async () => {
    setShowExpireModal(false);
    setExpiring(true);
    try {
      await apiClient.patch(`/assignments/${id}/expire`);
      // Refresh the assignment data to show updated status
      await fetchAssignment();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to expire assignment');
    } finally {
      setExpiring(false);
    }
  };

  const handleExpireCancel = () => {
    setShowExpireModal(false);
  };

  const handleSendLink = async () => {
    setSendingLink(true);
    try {
      const response = await apiClient.post(`/assignments/${id}/send-link`);
      const message = response.data?.sms?.message || 'Capture link sent by SMS';
      toastService.success(message);
    } catch (err) {
      toastService.error('Failed to send message. Please try again.');
    } finally {
      setSendingLink(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    setDeleting(true);
    try {
      await apiClient.delete(`/assignments/${id}`);
      toastService.success('Assignment deleted successfully');
      navigate('/assignments');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete assignment');
      toastService.error(err.response?.data?.error || 'Failed to delete assignment');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const fetchAssignment = useCallback(async () => {
    try {
      const response = await apiClient.get(`/assignments/${id}`);
      setAssignment(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load assignment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTimeline = useCallback(async () => {
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const res = await apiClient.get(`/assignments/${id}/timeline`);
      setTimeline(res.data.timeline || []);
    } catch (err) {
      setTimelineError(err.response?.data?.message || 'Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAssignment();
    fetchTimeline();
  }, [fetchAssignment, fetchTimeline]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading assignment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error/10 border border-error/20 text-error px-5 py-4 rounded-2xl text-xs font-black uppercase tracking-widest">{error}</div>
      </div>
    );
  }

  const completedCount = assignment.items?.filter(i => i.status === 'completed').length || 0;
  const totalCount = assignment.items?.length || 0;
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(assignment);

  return (
    <div className="">
      {viewer && <FullScreenViewer url={viewer.url} type={viewer.type} mimeType={viewer.mimeType} fileName={viewer.fileName} transcriptionUrl={viewer.transcriptionUrl} onClose={() => setViewer(null)} duration={viewer.duration} />}
      <button
        onClick={() => navigate('/assignments')}
        className="flex items-center gap-2 text-base-content/60 hover:text-primary mb-8 transition-all font-black uppercase tracking-widest text-[10px]"
      >
        <ArrowLeft size={16} />
        Back to Assignments
      </button>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Left Column: Details & Media */}
        <div className="w-full lg:w-[60%] space-y-6">
          <div className="glass-card rounded-xl p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-1 min-w-0">
                  <h1 className="page-title leading-none custom-line-break min-w-0">{assignment.submitterName}</h1>
                  {assignment.status === 'created' && (
                    <PermissionGuard permissions={['assignments.update']}>
                      <button
                        onClick={() => navigate(`/assignments/${id}/edit`)}
                        className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                        title="Edit Assignment"
                      >
                        <Edit2 size={16} />
                      </button>
                    </PermissionGuard>
                  )}
                  {(assignment.status === 'created' || assignment.status === 'expired') && (
                    <PermissionGuard permissions={['assignments.delete']}>
                      <button
                        onClick={handleDeleteClick}
                        className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                        title="Delete Assignment"
                      >
                        <Trash2 size={16} />
                      </button>
                    </PermissionGuard>
                  )}
                </div>
                <p className="page-subtitle custom-line-break">{assignment.workflowName}</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                {assignment.notes && assignment.notes.length > 0 && (
                  <button
                    onClick={() => setShowNotesModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 hover:bg-base-300 border border-base-300 text-xs font-bold text-base-content transition-colors"
                  >
                    <StickyNote size={14} />
                    Notes <span className="text-primary">({assignment.notes.length})</span>
                  </button>
                )}
                <StatusBadge status={assignment.status} />
              </div>
            </div>

            {totalCount > 0 && (
              <div className="mt-4">
                {/* Row 1: Creator, Customer Ref, Created */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mt-6">
                  {assignment.assigneeName && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">Creator Name</p>
                      <p className="text-sm text-base-content font-bold truncate bg-base-200 px-3 py-2.5 rounded-lg border border-base-300">
                        {assignment.assigneeName}
                      </p>
                    </div>
                  )}
                  {assignment.customerReferenceNumber && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">{dynamicCustomerReferenceLabel}</p>
                      <p className="font-mono text-xs text-base-content font-bold truncate bg-base-200 px-3 py-2.5 rounded-lg border border-base-300">
                        {assignment.customerReferenceNumber}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">Created Date</p>
                    <p className="text-sm text-base-content font-bold px-3 py-2.5 bg-base-200 rounded-lg border border-base-300">
                      {toLocal(assignment.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Row 2: System Reference Number, Link Expiry, Items */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mt-3">
                  {assignment.systemReferenceNumber && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">System Reference Number</p>
                      <p className="font-mono text-xs text-base-content font-bold truncate bg-base-200 px-3 py-2.5 rounded-lg border border-base-300">
                        {assignment.systemReferenceNumber}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">Link Expiry</p>
                    <p className="text-sm text-base-content font-bold px-3 py-2.5 bg-base-200 rounded-lg border border-base-300">
                      {toLocal(assignment.expiresAt, {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">Items</p>
                    <div className="text-primary text-sm font-black px-3 py-2.5 bg-primary/5 rounded-lg border border-primary/10 whitespace-nowrap overflow-hidden text-ellipsis">
                      {completedCount}/{totalCount} Completed
                    </div>
                  </div>
                </div>

                {/* Row 3: Recipient Name, Recipient Phone, Recipient Email */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mt-3">
                  {assignment.submitterName && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">Recipient Name</p>
                      <p className="text-sm text-base-content font-bold truncate bg-base-200 px-3 py-2.5 rounded-lg border border-base-300">
                        {assignment.submitterName}
                      </p>
                    </div>
                  )}
                  {assignment.submitterPhone && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">Recipient Phone</p>
                      <p className="text-sm text-base-content font-bold truncate bg-base-200 px-3 py-2.5 rounded-lg border border-base-300">
                        {assignment.submitterPhone}
                      </p>
                    </div>
                  )}
                  {assignment.submitterEmail && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-base-content/30 text-[10px] font-black uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">Recipient Email</p>
                      <p className="text-sm text-base-content font-bold truncate bg-base-200 px-3 py-2.5 rounded-lg border border-base-300" title={assignment.submitterEmail}>
                        {assignment.submitterEmail}
                      </p>
                    </div>
                  )}
                </div>

                {totalCount > 0 && (
                  <div className="mt-6">
                    <div className="w-full bg-base-content/10 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-success h-full transition-all duration-700 ease-out"
                        style={{ width: `${(completedCount / totalCount) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {assignment.secureToken && assignment.status !== 'completed' && assignment.status !== 'expired' && (
                  <div className="pt-5 border-t border-base-300">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em] flex items-center gap-1.5">
                        <Link size={12} />
                        Capture Link
                      </p>
                      <PermissionGuard permissions={['assignments.expire']}>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-black text-base-content/60 uppercase tracking-widest">
                            {expiring ? 'Expiring...' : 'Expire Link'}
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={assignment.status === 'expired'}
                              onClick={(e) => {
                                e.preventDefault();
                                if (assignment.status !== 'expired') {
                                  handleExpireToggle();
                                }
                              }}
                              disabled={expiring || assignment.status === 'expired'}
                              readOnly
                            />
                            <div className="relative w-11 h-6 bg-base-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-error/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-error peer-disabled:opacity-50 peer-disabled:cursor-not-allowed">
                              {expiring && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                </div>
                              )}
                            </div>
                          </label>
                        </div>
                      </PermissionGuard>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <div className="flex-1 w-full min-w-0">
                        <div className="bg-base-200 border border-base-300 rounded-lg px-3 py-2.5 text-xs font-mono text-base-content/60 custom-line-break mb-3">
                          {getCaptureLink(assignment)}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => handleCopy(getCaptureLink(assignment))}
                            className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 border border-base-content/10 text-base-content rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-base-content/10 transition-all active:scale-95"
                          >
                            {copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                            {copied ? 'Copied' : 'Copy Link'}
                          </button>
                          <button
                            onClick={handleSendLink}
                            disabled={sendingLink || !assignment.submitterPhone}
                            className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-primary text-primary-content rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={assignment.submitterPhone ? 'Send capture link by SMS' : 'Submitter phone is missing'}
                          >
                            {sendingLink ? (
                              <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin" />
                            ) : (
                              <Send size={16} />
                            )}
                            {sendingLink ? 'Sending' : 'Send SMS'}
                          </button>
                        </div>
                      </div>
                      {assignment?.subAccountShowQrCode !== false && (
                        <div className="w-full sm:w-auto flex justify-center p-4 bg-white border border-base-content/10 rounded-2xl shadow-inner">
                          <QRCodeDisplay value={getCaptureLink(assignment)} size={110} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex mt-4 disabled:opacity-60 items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                      Restrict to Single Session
                    </span>
                  </div>
                  <label className="relative inline-flex items-center disabled:opacity-60">
                    <input
                      id="restrict-single-session"
                      type="checkbox"
                      className="sr-only peer disabled:opacity-60"
                      checked={assignment.restrictToSingleSession}
                      disabled
                    />
                    <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                  </label>
                </div>
                <div className="flex mt-4 disabled:opacity-60 items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                      OTP verification
                    </span>
                  </div>
                  <label className="relative inline-flex items-center disabled:opacity-60">
                    <input
                      id="otp-verification"
                      type="checkbox"
                      className="sr-only peer disabled:opacity-60"
                      checked={assignment.requiredOtpVerification}
                      disabled
                    />
                    <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                  </label>
                </div>
                <div className="flex mt-4 disabled:opacity-60 items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                      Offline Support
                    </span>
                  </div>
                  <label className="relative inline-flex items-center disabled:opacity-60">
                    <input
                      id="offline-support-enabled"
                      type="checkbox"
                      className="sr-only peer disabled:opacity-60"
                      checked={assignment.offlineSupportEnabled}
                      disabled
                    />
                    <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                  </label>
                </div>
              </div>
            )}
          </div>

          {assignment.items && assignment.items.length > 0 && (
            <div className="bg-base-100 border border-base-300 rounded-2xl p-6 shadow-sm">
              <h3 className="font-black text-base-content uppercase tracking-tight text-sm mb-3 border-b border-base-content/5 pb-3">Capture Steps</h3>
              <div className="space-y-2">
                {assignment.items.map((item) => {
                  const Icon = typeIcons[item.type] || Camera;
                  const typeColors = {
                    photo: 'bg-primary/10 text-primary',
                    video: 'bg-secondary/10 text-secondary',
                  };
                  return (
                    <div key={item.id} className="flex items-center gap-3 py-3 border-b border-base-content/5 last:border-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${item.status === 'completed' ? 'bg-success/10' : 'bg-base-200'
                        }`}>
                        {item.status === 'completed'
                          ? <CheckCircle size={15} className="text-success" />
                          : <Clock size={15} className="text-base-content/40" />
                        }
                      </div>
                      <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColors[item.type] || 'bg-base-200 text-base-content/40'}`}>
                        {item.type === "identifier" ? <img src={scannerIcon} alt="" className='w-5 h-5 object-contain' /> : item.type === 'file' ? <img src={filesIcon} alt="" className='w-4 h-4 object-contain' /> : <Icon size={16} className="flex-shrink-0" />}
                      </div>
                      <span className="text-sm font-bold text-base-content flex-1 tracking-tight custom-line-break">{item.stepDescription}</span>
                      {!item.required && <span className="text-[10px] font-black uppercase tracking-widest text-base-content/20">Optional</span>}
                      {item.completedAt && (
                        <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                          {toLocal(item.completedAt, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {assignment.media && assignment.media.length > 0 && (
            <div className="pt-2">
              <h3 className="font-black text-base-content uppercase tracking-tight text-sm mb-3 border-b border-base-content/5 pb-3">
                Submitted Media <span className="text-primary ml-1 opacity-40">({assignment.media.length})</span>
              </h3>
              <div className={
                assignment.media.some(f => f.provenanceData && (f.provenanceData.network || f.provenanceData.device || f.provenanceData.location))
                  ? "space-y-4"
                  : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              }>
                {assignment.items
                  .filter(item => item.files && item.files.length > 0)
                  .map(item => {
                    // Use gallery carousel for any step with multiple previewable files
                    const isMultiPhoto = item.files.length > 1;

                    if (!isMultiPhoto) {
                      // Single file — render the existing card as before
                      const file = item.files[0];
                      const downloading = downloadingFiles[file.id] || false;

                      const handleDownload = async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDownloadingFiles(prev => ({ ...prev, [file.id]: true }));
                        try {
                          await triggerDownload(file.fileUrl, file.fileName);
                        } catch (err) {
                          console.error('Download failed:', err);
                        } finally {
                          setDownloadingFiles(prev => ({ ...prev, [file.id]: false }));
                        }
                      };

                      const fileHasProv = file.provenanceData && (file.provenanceData.network || file.provenanceData.device || file.provenanceData.location);

                      if (!fileHasProv) {
                        return <MediaCard key={file.id} file={file} onViewFullscreen={handleViewFullscreen} />;
                      }

                      return (
                        <ProvenanceMediaCard
                          key={file.id}
                          file={file}
                          downloading={downloading}
                          onDownload={handleDownload}
                          onViewFullscreen={handleViewFullscreen}
                        />
                      );
                    }

                    // Multi-file step — check provenance to decide layout (mirrors single-file branching)
                    const activeIndex = galleryIndexes[item.id] ?? 0;
                    const activeFile = item.files[activeIndex] ?? item.files[0];
                    const activeDownloading = downloadingFiles[activeFile.id] || false;
                    const activeProvenance = activeFile.provenanceData || {};
                    const stepHasProvenance = item.files.some(f => f.provenanceData && (f.provenanceData.network || f.provenanceData.device || f.provenanceData.location));

                    const handleDownloadActive = async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDownloadingFiles(prev => ({ ...prev, [activeFile.id]: true }));
                      try {
                        await triggerDownload(activeFile.fileUrl, activeFile.fileName);
                      } catch (err) {
                        console.error('Download failed:', err);
                      } finally {
                        setDownloadingFiles(prev => ({ ...prev, [activeFile.id]: false }));
                      }
                    };

                    const formatSize = (bytes) => {
                      if (!bytes) return 'Unknown size';
                      if (bytes < 1024) return `${bytes} B`;
                      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                    };

                    const renderGallerySlide = (photo) => {
                      const cat = getMediaCategory(photo.mimeType, photo.fileName);
                      if (cat === 'image') return <GallerySlideImage photo={photo} />;
                      return (
                        <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
                          <img src={fileAttachedPin} alt="attachment" className="w-[28px] h-[28px] shrink-0 theme-invert-icon" />
                          <span className="text-[15px] text-base-content truncate">{photo.fileName || 'file'}</span>
                        </div>
                      );
                    };

                    // ── No provenance: compact MediaCard-style (fits in grid) ──
                    if (!stepHasProvenance) {
                      return (
                        <div key={item.id} className={`bg-base-100 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all group relative ${activeFile.identifierNeedsReview ? 'border-2 border-error' : 'border-base-content/5 hover:border-primary/10'}`}>
                          {activeFile.identifierNeedsReview && (
                            <span
                              className="absolute top-2 left-2 z-20 bg-error text-error-content text-[9px] font-black uppercase tracking-wide px-2 py-1 rounded-md shadow-sm"
                              title={activeFile.identifierValidationError || 'This value was synced from offline capture and skipped validation.'}
                            >
                              Needs Review
                            </span>
                          )}
                          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all z-20">
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewFullscreen(activeFile.fileUrl, activeFile.fileType, activeFile.mimeType, activeFile.fileName, activeFile.duration); }} className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all" title="View Fullscreen"><Eye size={14} /></button>
                            <PermissionGuard permissions={['assignments.download_media']}>
                              <button onClick={handleDownloadActive} disabled={activeDownloading} className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all disabled:opacity-50" title="Download Media">
                                {activeDownloading ? <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Download size={14} />}
                              </button>
                            </PermissionGuard>
                          </div>
                          <div className="bg-base-900/5 aspect-[4/3] overflow-hidden relative">
                            <PhotoGallery photos={item.files} activeIndex={activeIndex} onIndexChange={(i) => handleIndexChange(item.id, i)} canPreview={false} renderSlide={renderGallerySlide} />
                          </div>
                          <div className="p-3">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                <div className="w-5 h-5 bg-primary/5 rounded flex items-center justify-center shrink-0"><Camera size={12} className="text-primary" /></div>
                                <span className="text-[10px] font-black text-base-content/60 uppercase tracking-tight truncate">{toLocal(activeProvenance.capturedAt || activeFile.createdAt, { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              <span className="text-[10px] font-bold text-base-content/40 uppercase tracking-wide shrink-0">{formatSize(activeFile.fileSize)}</span>
                            </div>
                            <h4 className="text-[11px] font-black text-base-content uppercase tracking-tight truncate">{item.stepDescription}</h4>
                            {activeFile.capturedByName && <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-1.5" title={activeFile.capturedByEmail || undefined}><User size={10} className="shrink-0" /><span className="truncate">Captured By: {activeFile.capturedByName}</span></div>}
                            {activeFile.identifierText && <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1"><span className="truncate" title={activeFile.identifierText}>Captured Value: {activeFile.identifierText}</span></div>}
                            {(activeFile.customerIdentifierText ?? activeFile.identifierText) && <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1"><span className="truncate" title={activeFile.customerIdentifierText ?? activeFile.identifierText}>Modified Value: {activeFile.customerIdentifierText ?? activeFile.identifierText}</span></div>}
                            {activeProvenance.location?.latitude && <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-2"><MapPin size={10} className="shrink-0" /><span className="truncate">{activeProvenance.location.latitude.toFixed(4)}, {activeProvenance.location.longitude.toFixed(4)}</span></div>}
                          </div>
                        </div>
                      );
                    }

                    // ── Has provenance: ProvenanceMediaCard-style split layout (full-width) ──
                    return (
                      <div key={item.id} className={`col-span-full bg-base-100 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${activeFile.identifierNeedsReview ? 'border-2 border-error' : 'border-base-content/5 hover:border-primary/10'}`}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">

                          {/* ── Left: Gallery + info strip ── */}
                          <div className="relative group">
                            {activeFile.identifierNeedsReview && (
                              <span
                                className="absolute top-2 left-2 z-30 bg-error text-error-content text-[9px] font-black uppercase tracking-wide px-2 py-1 rounded-md shadow-sm"
                                title={activeFile.identifierValidationError || 'This value was synced from offline capture and skipped validation.'}
                              >
                                Needs Review
                              </span>
                            )}
                            {/* Action buttons */}
                            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all z-30">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleViewFullscreen(activeFile.fileUrl, activeFile.fileType, activeFile.mimeType, activeFile.fileName, activeFile.duration);
                                }}
                                className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all"
                                title="View Fullscreen"
                              >
                                <Eye size={14} />
                              </button>
                              <PermissionGuard permissions={['assignments.download_media']}>
                                <button
                                  onClick={handleDownloadActive}
                                  disabled={activeDownloading}
                                  className="p-2 bg-base-100/90 backdrop-blur-sm rounded-lg text-base-content/60 hover:text-primary hover:bg-base-100 shadow-sm border border-base-content/10 transition-all disabled:opacity-50"
                                  title="Download Media"
                                >
                                  {activeDownloading ? (
                                    <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                  ) : (
                                    <Download size={14} />
                                  )}
                                </button>
                              </PermissionGuard>
                            </div>

                            {/* Carousel */}
                            <div className="bg-base-900/5 aspect-[4/3] overflow-hidden relative">
                              <PhotoGallery
                                photos={item.files}
                                activeIndex={activeIndex}
                                onIndexChange={(newIndex) => handleIndexChange(item.id, newIndex)}
                                canPreview={false}
                                renderSlide={renderGallerySlide}
                              />
                            </div>

                            {/* Info strip — updates with active slide */}
                            <div className="p-3 border-t border-base-content/5">
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <div className="w-5 h-5 bg-primary/5 rounded flex items-center justify-center shrink-0">
                                    <Camera size={12} className="text-primary" />
                                  </div>
                                  <span className="text-[10px] font-black text-base-content/60 uppercase tracking-tight truncate">
                                    {toLocal(activeProvenance.capturedAt || activeFile.createdAt, { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <span className="text-[10px] font-bold text-base-content/40 uppercase tracking-wide shrink-0">
                                  {formatSize(activeFile.fileSize)}
                                </span>
                              </div>
                              <h4 className="text-[11px] font-black text-base-content uppercase tracking-tight truncate">{item.stepDescription}</h4>
                              {activeFile.capturedByName && (
                                <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-1.5" title={activeFile.capturedByEmail || undefined}>
                                  <User size={10} className="shrink-0" />
                                  <span className="truncate">Captured By: {activeFile.capturedByName}</span>
                                </div>
                              )}
                              {activeFile.identifierText && (
                                <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1">
                                  <span className="truncate" title={activeFile.identifierText}>Captured Value: {activeFile.identifierText}</span>
                                </div>
                              )}
                              {(activeFile.customerIdentifierText ?? activeFile.identifierText) && (
                                <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/50 uppercase tracking-[0.15em] leading-none mt-1.5 bg-base-200 rounded px-2 py-1">
                                  <span className="truncate" title={activeFile.customerIdentifierText ?? activeFile.identifierText}>Modified Value: {activeFile.customerIdentifierText ?? activeFile.identifierText}</span>
                                </div>
                              )}
                              {activeProvenance.location?.latitude && (
                                <div className="flex items-center gap-1 text-[9px] font-bold text-base-content/30 uppercase tracking-[0.2em] leading-none mt-2">
                                  <MapPin size={10} className="shrink-0" />
                                  <span className="truncate">
                                    {activeProvenance.location.latitude.toFixed(4)}, {activeProvenance.location.longitude.toFixed(4)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ── Right: Provenance panel — updates with active slide ── */}
                          <div className="p-4 bg-base-50 border-t lg:border-t-0 lg:border-l border-base-content/5">
                            <h4 className="text-[10px] font-black text-base-content/30 uppercase tracking-widest mb-3">Provenance Data</h4>
                            <div className="space-y-3">
                              {activeProvenance.network && (
                                <div>
                                  <p className="text-[9px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Network</p>
                                  <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
                                    {activeProvenance.network.ipAddress && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">IP Address:</span>
                                        <span className="text-base-content font-mono font-semibold">{activeProvenance.network.ipAddress}</span>
                                      </div>
                                    )}
                                    {activeProvenance.network.connectionType && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">Connection:</span>
                                        <span className="text-base-content font-semibold uppercase">{activeProvenance.network.connectionType}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {activeProvenance.device && (
                                <div>
                                  <p className="text-[9px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Device</p>
                                  <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
                                    {activeProvenance.device.os && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">OS:</span>
                                        <span className="text-base-content font-semibold">{activeProvenance.device.os}</span>
                                      </div>
                                    )}
                                    {activeProvenance.device.browser && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">Browser:</span>
                                        <span className="text-base-content font-semibold">{activeProvenance.device.browser}</span>
                                      </div>
                                    )}
                                    {activeProvenance.device.deviceType && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">Type:</span>
                                        <span className="text-base-content font-semibold capitalize">{activeProvenance.device.deviceType}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {activeProvenance.location?.granted && (
                                <div>
                                  <p className="text-[9px] font-black text-base-content/30 uppercase tracking-widest mb-1.5">Location</p>
                                  <div className="bg-base-200 rounded-lg p-2.5 space-y-1">
                                    <div className="flex justify-between text-xs">
                                      <span className="text-base-content/60 font-medium">Coordinates:</span>
                                      <span className="text-base-content font-mono font-semibold text-[10px]">
                                        {activeProvenance.location.latitude.toFixed(6)}, {activeProvenance.location.longitude.toFixed(6)}
                                      </span>
                                    </div>
                                    {activeProvenance.location.accuracy && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">Accuracy:</span>
                                        <span className="text-base-content font-semibold">{Math.round(activeProvenance.location.accuracy)}m</span>
                                      </div>
                                    )}
                                    {activeProvenance.location.source && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-base-content/60 font-medium">Source:</span>
                                        <span className="text-base-content font-semibold uppercase">{activeProvenance.location.source}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {assignment.media && assignment.media.length === 0 && (
            <div className="glass-panel rounded-3xl p-12 text-center border-dashed border-2 border-base-content/10">
              <div className="w-16 h-16 bg-base-content/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-base-content/20" />
              </div>
              <p className="text-base-content/40 text-xs font-black uppercase tracking-widest">No media submitted yet</p>
            </div>
          )}
        </div>

        {/* Right Column: Timeline */}
        <div className="w-full lg:w-[40%] lg:sticky lg:top-4">
          <ProvenanceMetadata provenanceData={assignment.provenanceData} />
          <ProvenanceTimeline events={timeline} loading={timelineLoading} error={timelineError} />
        </div>
      </div>

      {/* Expire Link Confirmation Modal */}
      <Modal
        isOpen={showExpireModal}
        onClose={handleExpireCancel}
        title="Expire Assignment Link"
      >
        <div className="mb-6">
          <p className="text-base-content/60 text-sm">
            Are you sure you want to expire this assignment link? This action cannot be undone and will prevent any further submissions.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExpireCancel}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={expiring}
          >
            Cancel
          </button>
          <button
            onClick={handleExpireConfirm}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
            disabled={expiring}
          >
            {expiring ? 'Expiring...' : 'Expire Link'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title={`Notes (${assignment.notes?.length ?? 0})`}
      >
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {(assignment.notes || []).map(({ id, name, email, timestamp, note }) => (
            <NoteCard key={id} name={name} email={email} timestamp={timestamp} note={note} />
          ))}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete Assignment"
      >
        <div className="mb-6">
          <p className="text-base-content/60 text-sm">
            Are you sure you want to delete this assignment for {' '}
            <strong className="text-base-content">{assignment.submitterName}</strong>? This action cannot be undone. All associated media files and data will be permanently deleted.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleDeleteCancel}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Assignment'}
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default ReviewMedia;
